import { useParams, useSearchParams } from "react-router";
import { useCustomerLinkAdapter } from "@shared-core/data/CustomerLinkAdapterContext";
import {
  GoneCard,
  LinkErrorCard,
  LinkLayout,
  LinkLoadingCard,
  LinkNotFoundCard,
} from "./LinkLayout";
import { OrderPageBody, type OrderArrival } from "./OrderPageBody";
import { useGoTo, useLinkRead } from "./useLinkRead";

/** `/order/:token`: the customer's order by their emailed link, no sign-in. Arrives with `?placed=1` straight
 *  after a quote's acceptance, or with Stripe's `?payment=return` after a card checkout. */
export function OrderLinkPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const arrival: OrderArrival =
    searchParams.get("placed") === "1"
      ? "placed"
      : searchParams.get("payment") === "return"
        ? "payment_return"
        : null;
  const adapter = useCustomerLinkAdapter();
  const goTo = useGoTo();
  // A return from the card checkout reads the order again (the same route, so no remount).
  const { state, reload } = useLinkRead(
    `${token}|${arrival ?? ""}`,
    () => adapter.getOrder(token),
    "We couldn't load this order.",
  );

  return (
    <LinkLayout
      signedInWhere="Orders"
      isWide
      legalDetails={state.kind === "ok" ? state.view.legal_details : null}
    >
      {state.kind === "loading" ? (
        <LinkLoadingCard what="order" />
      ) : state.kind === "error" ? (
        <LinkErrorCard
          what="order"
          message={state.message}
          onRetry={() => void reload()}
        />
      ) : state.kind === "gone" ? (
        <GoneCard sentence={state.sentence} what="orders" />
      ) : state.kind === "not_found" ? (
        <LinkNotFoundCard />
      ) : (
        <OrderPageBody
          page={state.view}
          arrival={arrival}
          loadKitDrops={adapter.getKitDrops}
          actions={{
            pay: async (addedLineRef) => {
              const { checkout_url } = await adapter.startOrderCardPayment(
                token,
                addedLineRef,
              );
              goTo(checkout_url);
            },
            givePoNumber: (number) => adapter.giveOrderPoNumber(token, number),
            reload,
            writes: adapter.artwork,
            fetchFile: adapter.fetchFile,
          }}
        />
      )}
    </LinkLayout>
  );
}
